// Linked boards (Account / Grafici / Strategist / Website): one card, one status, filtered views.
// The board -> statuses map mirrors the backend `LINKED_BOARD_COLUMNS`: keep them identical.

export const LINKED_BOARD_IDS = {
  ACCOUNT: "ACCOUNT",
  GRAPHICS: "GRAPHICS",
  STRATEGIST: "STRATEGIST",
  WEBSITE: "WEBSITE",
};

// URL slug (lowercase) <-> board id sent to the API
export const LINKED_BOARD_SLUGS = {
  account: LINKED_BOARD_IDS.ACCOUNT,
  graphics: LINKED_BOARD_IDS.GRAPHICS,
  strategist: LINKED_BOARD_IDS.STRATEGIST,
  website: LINKED_BOARD_IDS.WEBSITE,
};

export const getLinkedBoardSlug = (boardId) => String(boardId || "").toLowerCase();

export const getLinkedBoardIdFromSlug = (slug) =>
  LINKED_BOARD_SLUGS[String(slug || "").toLowerCase()] || null;

// Sidebar order
export const LINKED_BOARD_LIST = [
  LINKED_BOARD_IDS.ACCOUNT,
  LINKED_BOARD_IDS.GRAPHICS,
  LINKED_BOARD_IDS.STRATEGIST,
  LINKED_BOARD_IDS.WEBSITE,
];

// Boards where cards are created (the others only move them)
export const LINKED_BOARD_CREATION = [LINKED_BOARD_IDS.ACCOUNT, LINKED_BOARD_IDS.WEBSITE];

export const LINKED_BOARD_STATUSES = {
  NEW_CLIENT: "NEW_CLIENT",
  FIRST_MONTH: "FIRST_MONTH",
  TODO: "TODO",
  IN_REVIEW: "IN_REVIEW",
  TO_EDIT: "TO_EDIT",
  TO_UPLOAD_POST: "TO_UPLOAD_POST",
  TO_UPLOAD_ADS: "TO_UPLOAD_ADS",
  PROBLEMS: "PROBLEMS",
  COMPLETED: "COMPLETED",
  WEBSITE_BRIEF: "WEBSITE_BRIEF",
  WEBSITE_DEV: "WEBSITE_DEV",
  WEBSITE_REVIEW: "WEBSITE_REVIEW",
  WEBSITE_LIVE: "WEBSITE_LIVE",
};

// Statuses (= columns) shown by each board, in column order
export const LINKED_BOARD_COLUMNS = {
  [LINKED_BOARD_IDS.ACCOUNT]: [
    "NEW_CLIENT",
    "FIRST_MONTH",
    "TODO",
    "IN_REVIEW",
    "TO_EDIT",
    "TO_UPLOAD_POST",
    "TO_UPLOAD_ADS",
    "PROBLEMS",
    "COMPLETED",
  ],
  [LINKED_BOARD_IDS.GRAPHICS]: ["FIRST_MONTH", "TODO", "IN_REVIEW", "TO_EDIT"],
  [LINKED_BOARD_IDS.STRATEGIST]: ["TO_UPLOAD_POST", "TO_UPLOAD_ADS", "PROBLEMS", "COMPLETED"],
  // Its own columns: a website card is not in the other boards
  [LINKED_BOARD_IDS.WEBSITE]: ["WEBSITE_BRIEF", "WEBSITE_DEV", "WEBSITE_REVIEW", "WEBSITE_LIVE"],
};

export const LINKED_BOARD_COLUMN_COLORS = {
  NEW_CLIENT: "#4976F4",
  FIRST_MONTH: "#8B5CF6",
  TODO: "#F59E0B",
  IN_REVIEW: "#06B6D4",
  TO_EDIT: "#EF6C00",
  TO_UPLOAD_POST: "#0EA5E9",
  TO_UPLOAD_ADS: "#14B8A6",
  PROBLEMS: "#DC2626",
  COMPLETED: "#589E67",
  WEBSITE_BRIEF: "#4976F4",
  WEBSITE_DEV: "#F59E0B",
  WEBSITE_REVIEW: "#06B6D4",
  WEBSITE_LIVE: "#589E67",
};

// The three assignee roles of a card (card field name)
export const LINKED_BOARD_ROLES = ["account", "grafico", "strategist"];

/** Display name of an operator, from a card ({full_name}) or from the operators list ({first_name, last_name}). */
export const getOperatorName = (operator) => {
  if (!operator) return "";
  if (operator.full_name) return operator.full_name;
  const name = [operator.first_name, operator.last_name].filter(Boolean).join(" ").trim();
  return name || operator.email || operator.username || "";
};

export const getOperatorInitials = (operator) => {
  const name = getOperatorName(operator);
  if (!name) return "?";
  const parts = name.split(/[\s@.]+/).filter(Boolean);
  return ((parts[0]?.[0] || "") + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase() || "?";
};

/** Cards of a status sorted by `order` (ties by creation date). */
export const sortCardsByOrder = (cards) =>
  [...cards].sort(
    (a, b) =>
      parseFloat(a.order || 0) - parseFloat(b.order || 0) ||
      String(a.created_at || "").localeCompare(String(b.created_at || ""))
  );

/**
 * Cards of a status as shown in the column: nearest due date on top, cards without a due date
 * at the bottom (ties by `order`).
 */
export const sortCardsByDueDate = (cards) =>
  [...cards].sort((a, b) => {
    const aDate = a.due_date || "";
    const bDate = b.due_date || "";
    if (aDate !== bDate) {
      if (!aDate) return 1;
      if (!bDate) return -1;
      return aDate.localeCompare(bDate);
    }
    return (
      parseFloat(a.order || 0) - parseFloat(b.order || 0) ||
      String(a.created_at || "").localeCompare(String(b.created_at || ""))
    );
  });

/** Lightweight identity of a list of cards: lets a poll skip the store update when nothing changed. */
export const cardsSignature = (cards) =>
  (cards || [])
    // A job card also changes when its Marketing Mind proposal does (state, draft image)
    .map(
      (c) =>
        `${c.id}|${c.status}|${c.order}|${c.updated_at}|${c.mm_job?.status || ""}|${
          c.mm_job?.generation_status || ""
        }|${c.mm_job?.image?.file || ""}`
    )
    .join(",");
